import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/** Sunday-first by default — override with `weekStartsOn`. */
const WEEKDAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const WEEKS_SHOWN = 6;
const DAYS_IN_WEEK = 7;

/**
 * One thing on the calendar.
 *
 * `date` is where it renders solid. `linkedDate` is the OTHER date it also
 * belongs to — rendered faint with an arrow — for events whose cause and
 * effect fall on different days (an accrual and its payment, a start date and
 * the pay run that carries it). Leave it unset for single-date events.
 */
export interface EventCalendarEvent {
  id: string;
  title: string;
  /** ISO `YYYY-MM-DD`. The day this renders solid. */
  date: string;
  /** ISO `YYYY-MM-DD`. Renders faint + arrowed. Ignored when equal to `date`. */
  linkedDate?: string | null;
  /** Short trailing text on the solid chip — an amount, a count. */
  trailing?: string;
  /** Tone classes for the chip (border/bg/text). */
  chipClassName?: string;
  tooltip?: string;
  /** Tooltip for the faint chip; falls back to `tooltip`. */
  linkedTooltip?: string;
}

export interface EventCalendarProps {
  year: number;
  /** 1-12. */
  month: number;
  events: EventCalendarEvent[];
  onPrev: () => void;
  onNext: () => void;
  /** Omit to hide the Today button. */
  onToday?: () => void;
  onSelectEvent?: (event: EventCalendarEvent) => void;
  /** Clicking empty space in a day cell. Omit to make cells inert. */
  onSelectDay?: (isoDate: string) => void;
  /** 0 = Sunday (default), 1 = Monday. */
  weekStartsOn?: 0 | 1;
  /** Explanatory line under the grid. */
  footnote?: React.ReactNode;
  /** Trailing controls in the header, beside the month name. */
  headerActions?: React.ReactNode;
  className?: string;
}

/** Parse `YYYY-MM-DD` as a LOCAL date. `new Date(str)` reads a bare date as
 *  UTC, which shifts every cell a day west of Greenwich. */
export function parseIsoDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

export function toIsoDate(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/** Step a (year, month) pair by whole months, rolling the year over. */
export function shiftMonth(
  year: number,
  month: number,
  delta: number,
): { year: number; month: number } {
  const zero = year * 12 + (month - 1) + delta;
  return { year: Math.floor(zero / 12), month: (zero % 12) + 1 };
}

export function monthTitle(year: number, month: number): string {
  return `${MONTH_NAMES[month - 1] ?? month} ${year}`;
}

/** The day grid for a month, including the leading/trailing days that pad the
 *  first and last weeks out to whole rows. */
export function monthGrid(year: number, month: number, weekStartsOn: 0 | 1 = 0): Date[] {
  const first = new Date(year, month - 1, 1);
  const offset = (first.getDay() - weekStartsOn + DAYS_IN_WEEK) % DAYS_IN_WEEK;
  const cursor = new Date(year, month - 1, 1 - offset);
  const lastOfMonth = new Date(year, month, 0);
  const days: Date[] = [];
  for (let w = 0; w < WEEKS_SHOWN; w++) {
    for (let d = 0; d < DAYS_IN_WEEK; d++) {
      days.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    // Stop once the month is fully covered and the row is complete.
    if (cursor > lastOfMonth) break;
  }
  return days;
}

interface DayBucket {
  solid: EventCalendarEvent[];
  faint: EventCalendarEvent[];
}

function bucketByDay(events: EventCalendarEvent[]): Map<string, DayBucket> {
  const out = new Map<string, DayBucket>();
  const at = (iso: string): DayBucket => {
    let entry = out.get(iso);
    if (!entry) {
      entry = { solid: [], faint: [] };
      out.set(iso, entry);
    }
    return entry;
  };
  for (const e of events) {
    at(e.date).solid.push(e);
    if (e.linkedDate && e.linkedDate !== e.date) at(e.linkedDate).faint.push(e);
  }
  return out;
}

/**
 * A month calendar of dated events.
 *
 * Distinct from `DatePicker`'s calendar, which picks a date. This one DISPLAYS
 * things that happen on dates, and its one opinion is that an event can belong
 * to two days: the day it happens and the day its effect lands. Those render
 * as two chips — solid on `date`, faint and arrowed on `linkedDate` — so the
 * gap between cause and effect is visible rather than implied.
 *
 * Tone is the caller's job: pass `chipClassName` per event. The component owns
 * layout, the two-date rule, and keyboard/click behaviour.
 */
export function EventCalendar({
  year,
  month,
  events,
  onPrev,
  onNext,
  onToday,
  onSelectEvent,
  onSelectDay,
  weekStartsOn = 0,
  footnote,
  headerActions,
  className,
}: EventCalendarProps) {
  const byDay = useMemo(() => bucketByDay(events), [events]);
  const days = useMemo(
    () => monthGrid(year, month, weekStartsOn),
    [year, month, weekStartsOn],
  );
  const weekdays = useMemo(
    () => [
      ...WEEKDAY_NAMES.slice(weekStartsOn),
      ...WEEKDAY_NAMES.slice(0, weekStartsOn),
    ],
    [weekStartsOn],
  );
  const todayIso = toIsoDate(new Date());

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" onClick={onPrev} aria-label="Previous month">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={onNext} aria-label="Next month">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <h2 className="ml-2 text-lg font-semibold">{monthTitle(year, month)}</h2>
        </div>
        <div className="flex items-center gap-2">
          {headerActions}
          {onToday && (
            <Button variant="ghost" size="sm" onClick={onToday}>
              Today
            </Button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[720px]">
          <div className="grid grid-cols-7 border-b border-border">
            {weekdays.map((d) => (
              <div
                key={d}
                className="px-2 py-1.5 text-center text-xs font-medium text-muted-foreground"
              >
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7">
            {days.map((day) => {
              const iso = toIsoDate(day);
              const inMonth = day.getMonth() === month - 1;
              const bucket = byDay.get(iso);
              const Cell = onSelectDay ? "button" : "div";
              return (
                <Cell
                  key={iso}
                  {...(onSelectDay
                    ? { type: "button" as const, onClick: () => onSelectDay(iso) }
                    : {})}
                  className={cn(
                    "flex min-h-[104px] flex-col gap-1 border-b border-r border-border p-1.5 text-left align-top",
                    onSelectDay && "transition-colors hover:bg-muted/40",
                    !inMonth && "bg-muted/30",
                  )}
                >
                  <span
                    className={cn(
                      "text-xs font-medium",
                      iso === todayIso &&
                        "inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand text-white",
                      !inMonth && "text-muted-foreground",
                    )}
                  >
                    {day.getDate()}
                  </span>

                  {bucket?.solid.map((e) => (
                    <EventChip key={`s-${e.id}`} event={e} onSelect={onSelectEvent} />
                  ))}
                  {bucket?.faint.map((e) => (
                    <EventChip key={`f-${e.id}`} event={e} onSelect={onSelectEvent} faint />
                  ))}
                </Cell>
              );
            })}
          </div>
        </div>
      </div>

      {footnote && <div className="text-xs text-muted-foreground">{footnote}</div>}
    </div>
  );
}

function EventChip({
  event,
  onSelect,
  faint = false,
}: {
  event: EventCalendarEvent;
  onSelect?: (event: EventCalendarEvent) => void;
  faint?: boolean;
}) {
  const interactive = !!onSelect;
  return (
    <span
      {...(interactive
        ? {
            role: "button" as const,
            tabIndex: 0,
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              onSelect(event);
            },
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                e.preventDefault();
                onSelect(event);
              }
            },
          }
        : {})}
      title={(faint ? event.linkedTooltip : event.tooltip) ?? event.tooltip}
      className={cn(
        "flex w-full items-center gap-1 truncate rounded border px-1 py-0.5 text-[11px] leading-tight",
        event.chipClassName ?? "border-border bg-muted",
        faint && "opacity-50",
      )}
    >
      {faint && <ArrowRight className="h-2.5 w-2.5 shrink-0" />}
      <span className="truncate font-medium">{event.title}</span>
      {!faint && event.trailing && (
        <span className="ml-auto shrink-0 tabular-nums">{event.trailing}</span>
      )}
    </span>
  );
}
